import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import {
  CustomerAdvance,
  CustomerAdvanceVm,
  CustomerAdvanceListResponse,
  Account,
  ApiResponse,
} from '@/api/types'
import { appendSortDir, type ListSortDir } from '@/lib/listSort'

/**
 * Customer Advance management service
 * Handles all customer advance-related API operations
 */
export class CustomerAdvanceService {
  /**
   * Get paginated list of customer advances for a branch
   */
  async getAllAdvances(
    branchId: number,
    pageNumber: number,
    pageSize: number,
    sortDir: ListSortDir = 'desc'
  ): Promise<CustomerAdvanceListResponse> {
    const url = appendSortDir(endpoints.customerAdvances.getAll(branchId, pageNumber, pageSize), sortDir)
    const response = await apiClient.get<ApiResponse<CustomerAdvanceListResponse> | CustomerAdvanceListResponse | any>(url)
    const payload = response.data

    // Helper to map advances array
    const mapAdvances = (advances: any[]): CustomerAdvance[] => {
      return advances.map(adv => this.mapAdvanceData(adv))
    }

    if (payload && typeof payload === 'object') {
      if ('success' in payload && 'data' in payload) {
        const data = payload.data
        if (Array.isArray(data)) {
          return {
            totalRecords: payload.totalRecords ?? data.length,
            pageNumber: payload.pageNumber ?? pageNumber,
            pageSize: payload.pageSize ?? pageSize,
            data: mapAdvances(data),
          }
        }
        return data as CustomerAdvanceListResponse
      }

      if ('data' in payload && Array.isArray(payload.data)) {
        return {
          totalRecords: payload.totalRecords ?? payload.data.length,
          pageNumber: payload.pageNumber ?? pageNumber,
          pageSize: payload.pageSize ?? pageSize,
          data: mapAdvances(payload.data),
        }
      }
    }

    if (Array.isArray(payload)) {
      return {
        totalRecords: payload.length,
        pageNumber,
        pageSize,
        data: mapAdvances(payload),
      }
    }

    return {
      totalRecords: payload?.totalRecords ?? 0,
      pageNumber: payload?.pageNumber ?? pageNumber,
      pageSize: payload?.pageSize ?? pageSize,
      data: payload?.data ? mapAdvances(payload.data) : [],
    }
  }

  /**
   * Get customer advance by ID
   */
  async getAdvanceById(branchId: number, id: number): Promise<CustomerAdvance> {
    const url = endpoints.customerAdvances.getById(branchId, id)
    const response = await apiClient.get<ApiResponse<CustomerAdvance> | CustomerAdvance | any>(url)
    
    let advanceData: any
    
    if ('data' in response.data && 'success' in response.data) {
      advanceData = response.data.data
    } else if ('data' in response.data) {
      advanceData = response.data.data
    } else {
      advanceData = response.data
    }
    
    return this.mapAdvanceData(advanceData)
  }

  /**
   * Get all accounts for a branch
   */
  async getAllAccounts(branchId: number): Promise<Account[]> {
    const url = endpoints.customerAdvances.getAllAccounts(branchId)
    const response = await apiClient.get<ApiResponse<Account[]> | Account[] | any>(url)
    
    let accountsData: any[] = []
    
    if (Array.isArray(response.data)) {
      accountsData = response.data
    } else if ('data' in response.data && Array.isArray(response.data.data)) {
      accountsData = response.data.data
    }
    
    // Map to Account format
    return accountsData.map((item: any) => ({
      id: item.id || item.accountId || 0,
      accountId: item.accountId || item.id || 0,
      accountName: item.accountName || item.accountNameEn || item.name || '',
      accountNameEn: item.accountNameEn || item.accountName || item.nameEn || '',
      accountNameAr: item.accountNameAr || item.nameAr || '',
      accountCode: item.accountCode || item.code || '',
    }))
  }

  /**
   * Get user IP address
   */
  private async getUserIPAddress(): Promise<string> {
    try {
      const response = await fetch('https://api.ipify.org?format=json')
      const data = await response.json()
      return data.ip || '0.0.0.0'
    } catch (error) {
      console.warn('Failed to get IP address from service, using fallback')
      return '0.0.0.0'
    }
  }

  /**
   * Insert customer advance
   */
  async insertAdvance(advance: CustomerAdvanceVm): Promise<CustomerAdvance> {
    const url = endpoints.customerAdvances.insert
    
    // Get IP address if not provided
    const advanceWithIP = {
      ...advance,
      ipAddress: advance.ipAddress || await this.getUserIPAddress(),
    }
    
    console.log('=== CustomerAdvanceService.insertAdvance ===')
    console.log('Endpoint:', url)
    console.log('Payload:', JSON.stringify(advanceWithIP, null, 2))
    console.log('Payload Object:', advanceWithIP)
    console.log('==============================================')
    
    const response = await apiClient.post<ApiResponse<CustomerAdvance> | CustomerAdvance>(url, advanceWithIP)
    
    console.log('=== API Response ===')
    console.log('Response Status:', response.status)
    console.log('Response Data:', JSON.stringify(response.data, null, 2))
    console.log('====================')
    
    if ('data' in response.data && 'success' in response.data) {
      return this.mapAdvanceData((response.data as ApiResponse<CustomerAdvance>).data)
    }
    
    return this.mapAdvanceData(response.data as CustomerAdvance)
  }

  /**
   * Update customer advance
   */
  async updateAdvance(advance: CustomerAdvanceVm): Promise<CustomerAdvance> {
    const url = endpoints.customerAdvances.update
    
    // Get IP address if not provided
    const advanceWithIP = {
      ...advance,
      ipAddress: advance.ipAddress || await this.getUserIPAddress(),
    }
    
    console.log('=== CustomerAdvanceService.updateAdvance ===')
    console.log('Endpoint:', url)
    console.log('Payload:', JSON.stringify(advanceWithIP, null, 2))
    console.log('Payload Object:', advanceWithIP)
    console.log('============================================')
    
    const response = await apiClient.put<ApiResponse<CustomerAdvance> | CustomerAdvance>(url, advanceWithIP)
    
    console.log('=== API Response ===')
    console.log('Response Status:', response.status)
    console.log('Response Data:', JSON.stringify(response.data, null, 2))
    console.log('====================')
    
    if ('data' in response.data && 'success' in response.data) {
      return this.mapAdvanceData((response.data as ApiResponse<CustomerAdvance>).data)
    }
    
    return this.mapAdvanceData(response.data as CustomerAdvance)
  }

  /**
   * Map API response to CustomerAdvance interface
   */
  private mapAdvanceData(advanceData: any): CustomerAdvance {
    return {
      id: advanceData.id || 0,
      advNo: advanceData.advNo || advanceData.advanceNo || '',
      customerId: advanceData.customerId || 0,
      customerName: advanceData.customerName || advanceData.customerNameEn || advanceData.customerNameAr || '',
      customerNameEn: advanceData.customerNameEn || advanceData.customerName || '',
      customerNameAr: advanceData.customerNameAr || '',
      invoiceRef: advanceData.invoiceRef || advanceData.invoiceReference || '',
      receiptRef: advanceData.receiptRef || advanceData.receiptReference || '',
      date: advanceData.date || '',
      amount: advanceData.amount || 0,
      debitAccount: advanceData.debitAccount || advanceData.debitAccountId || 0,
      debitAccountName: advanceData.debitAccountName || '',
      paymentType: advanceData.paymentType || '',
      refNo: advanceData.refNo || advanceData.refNumber || '',
      note: advanceData.note || '',
      branchId: advanceData.branchId || 0,
      ipAddress: advanceData.ipAddress || '',
    } as CustomerAdvance
  }
}

export const customerAdvanceService = new CustomerAdvanceService()

